import { GestureShape } from '../gestures/GestureDetector';
import { SpellType } from '../game/CooldownManager';

export interface TutorialStep {
  spell: SpellType;
  gesture: GestureShape;
  title: string;
  description: string;
  icon: string;
  color: string;
  /** SVG path data for the gesture diagram */
  diagramSvg: string;
}

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    spell: 'fireball',
    gesture: 'circle',
    title: 'Fireball',
    description: 'Draw a circle in the air with your index finger',
    icon: '\u{1F525}',
    color: '#FF6B35',
    diagramSvg: `<circle cx="60" cy="60" r="40" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="8 4" />
      <path d="M60 20 A40 40 0 1 1 59.9 20" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" class="gesture-anim-circle" />
      <circle cx="60" cy="20" r="5" fill="currentColor" class="gesture-anim-dot" />`
  },
  {
    spell: 'iceShard',
    gesture: 'swipe-down',
    title: 'Ice Shard',
    description: 'Swipe your hand downward quickly',
    icon: '\u{2744}\u{FE0F}',
    color: '#87CEEB',
    diagramSvg: `<line x1="60" y1="20" x2="60" y2="100" stroke="currentColor" stroke-width="3" stroke-dasharray="8 4" />
      <path d="M60 20 L60 100" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" class="gesture-anim-swipe-down" />
      <polygon points="50,85 60,105 70,85" fill="currentColor" class="gesture-anim-arrow-down" />
      <circle cx="60" cy="20" r="5" fill="currentColor" class="gesture-anim-dot" />`
  },
  {
    spell: 'shield',
    gesture: 'palm-hold',
    title: 'Shield',
    description: 'Hold your open palm steady for a moment',
    icon: '\u{1F6E1}\u{FE0F}',
    color: '#C9A227',
    diagramSvg: `<rect x="25" y="25" width="70" height="70" rx="8" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="8 4" />
      <path d="M40 45 L40 75 M50 40 L50 75 M60 35 L60 75 M70 40 L70 75 M80 45 L80 75" stroke="currentColor" stroke-width="3" stroke-linecap="round" class="gesture-anim-palm" />
      <ellipse cx="60" cy="80" rx="25" ry="10" fill="none" stroke="currentColor" stroke-width="2" opacity="0.5" />`
  },
  {
    spell: 'lightningBolt',
    gesture: 'zigzag',
    title: 'Lightning Bolt',
    description: 'Make a rapid zigzag motion with your finger',
    icon: '\u{26A1}',
    color: '#FFD700',
    diagramSvg: `<polyline points="30,20 70,45 30,70 70,95" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="8 4" />
      <polyline points="30,20 70,45 30,70 70,95" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" class="gesture-anim-zigzag" />
      <circle cx="30" cy="20" r="5" fill="currentColor" class="gesture-anim-dot" />`
  },
  {
    spell: 'healSpell',
    gesture: 'swipe-up',
    title: 'Heal',
    description: 'Swipe your hand upward quickly',
    icon: '\u{1F49A}',
    color: '#44FF44',
    diagramSvg: `<line x1="60" y1="100" x2="60" y2="20" stroke="currentColor" stroke-width="3" stroke-dasharray="8 4" />
      <path d="M60 100 L60 20" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" class="gesture-anim-swipe-up" />
      <polygon points="50,35 60,15 70,35" fill="currentColor" class="gesture-anim-arrow-up" />
      <circle cx="60" cy="100" r="5" fill="currentColor" class="gesture-anim-dot" />`
  }
];

const STORAGE_KEY = 'wizard_duel_tutorial_complete';

export class TutorialManager {
  private container: HTMLElement;
  private currentStep: number = 0;
  private tutorialElement: HTMLElement | null = null;
  private isActive: boolean = false;
  private onComplete: (() => void) | null = null;
  private onRequestCamera: (() => Promise<boolean>) | null = null;
  private onSetPracticeMode: ((enabled: boolean) => void) | null = null;
  private audioCallback: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  setCallbacks(callbacks: {
    onComplete: () => void;
    onRequestCamera: () => Promise<boolean>;
    onSetPracticeMode: (enabled: boolean) => void;
    onGestureConfirmSound: () => void;
  }): void {
    this.onComplete = callbacks.onComplete;
    this.onRequestCamera = callbacks.onRequestCamera;
    this.onSetPracticeMode = callbacks.onSetPracticeMode;
    this.audioCallback = callbacks.onGestureConfirmSound;
  }

  static isTutorialComplete(): boolean {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  }

  static markComplete(): void {
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  static resetTutorial(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  async start(): Promise<void> {
    this.isActive = true;
    this.currentStep = 0;

    // Enable practice mode
    if (this.onSetPracticeMode) {
      this.onSetPracticeMode(true);
    }

    // Initialize camera
    const webcamContainer = document.getElementById('webcam-container');
    if (this.onRequestCamera) {
      const success = await this.onRequestCamera();
      if (success && webcamContainer) {
        webcamContainer.style.display = 'block';
      }
    }

    this.render();
  }

  stop(): void {
    this.isActive = false;

    if (this.onSetPracticeMode) {
      this.onSetPracticeMode(false);
    }

    const webcamContainer = document.getElementById('webcam-container');
    if (webcamContainer) {
      webcamContainer.style.display = 'none';
    }

    if (this.tutorialElement) {
      this.tutorialElement.remove();
      this.tutorialElement = null;
    }
  }

  /** Called by the game when a gesture is detected during tutorial */
  onGestureDetected(spell: SpellType, info?: { shape: GestureShape }): void {
    if (!this.isActive) return;

    const step = TUTORIAL_STEPS[this.currentStep];
    if (!step) return;

    // Check if the detected gesture matches the current step
    const matchesSpell = spell === step.spell;
    const matchesGesture = info?.shape === step.gesture;

    if (matchesSpell || matchesGesture) {
      this.onStepSuccess();
    }
  }

  private onStepSuccess(): void {
    // Play success sound
    if (this.audioCallback) {
      this.audioCallback();
    }

    // Show success animation on the current step
    const stepContent = this.tutorialElement?.querySelector('.tutorial-step-content');
    if (stepContent) {
      stepContent.classList.add('step-success');
    }

    // Flash webcam border green
    const webcamContainer = document.getElementById('webcam-container');
    if (webcamContainer) {
      webcamContainer.classList.add('gesture-success');
      setTimeout(() => {
        webcamContainer.classList.remove('gesture-success');
      }, 500);
    }

    // Advance after a brief delay
    setTimeout(() => {
      this.currentStep++;
      if (this.currentStep >= TUTORIAL_STEPS.length) {
        this.completeTutorial();
      } else {
        this.render();
      }
    }, 1000);
  }

  private completeTutorial(): void {
    TutorialManager.markComplete();
    this.showCompletionScreen();
  }

  private showCompletionScreen(): void {
    if (!this.tutorialElement) return;

    this.tutorialElement.innerHTML = `
      <div class="tutorial-completion">
        <h2 class="tutorial-title">Well Done!</h2>
        <div class="ornament"></div>
        <p class="tutorial-complete-msg">You have mastered the five arcane gestures.</p>
        <p class="tutorial-complete-sub">You are ready to enter the arena.</p>
        <div class="tutorial-buttons">
          <button class="menu-button" id="tutorial-practice-btn">Practice Arena</button>
          <button class="menu-button" id="tutorial-done-btn">Return to Menu</button>
        </div>
      </div>
    `;

    this.tutorialElement.querySelector('#tutorial-practice-btn')?.addEventListener('click', () => {
      this.stop();
      // TODO: Start practice arena match - for now return to menu
      if (this.onComplete) this.onComplete();
    });

    this.tutorialElement.querySelector('#tutorial-done-btn')?.addEventListener('click', () => {
      this.stop();
      if (this.onComplete) this.onComplete();
    });
  }

  private render(): void {
    const step = TUTORIAL_STEPS[this.currentStep];
    if (!step) return;

    if (!this.tutorialElement) {
      this.tutorialElement = document.createElement('div');
      this.tutorialElement.className = 'tutorial-screen';
      this.container.appendChild(this.tutorialElement);
    }

    const totalSteps = TUTORIAL_STEPS.length;

    this.tutorialElement.innerHTML = `
      <div class="tutorial-header">
        <h2 class="tutorial-title">Spell Training</h2>
        <div class="tutorial-progress">
          <div class="tutorial-progress-text">Step ${this.currentStep + 1} of ${totalSteps}</div>
          <div class="tutorial-progress-bar">
            <div class="tutorial-progress-fill" style="width: ${((this.currentStep) / totalSteps) * 100}%"></div>
          </div>
          <div class="tutorial-progress-dots">
            ${TUTORIAL_STEPS.map((s, i) => `
              <div class="tutorial-dot ${i < this.currentStep ? 'completed' : ''} ${i === this.currentStep ? 'active' : ''}" style="--dot-color: ${s.color}"></div>
            `).join('')}
          </div>
        </div>
      </div>
      <div class="tutorial-step-content" style="--step-color: ${step.color}">
        <div class="tutorial-gesture-diagram" style="color: ${step.color}">
          <svg viewBox="0 0 120 120" width="120" height="120">
            ${step.diagramSvg}
          </svg>
        </div>
        <div class="tutorial-spell-icon">${step.icon}</div>
        <h3 class="tutorial-spell-name" style="color: ${step.color}">${step.title}</h3>
        <p class="tutorial-instruction">${step.description}</p>
        <div class="tutorial-waiting">
          <div class="tutorial-waiting-pulse" style="border-color: ${step.color}"></div>
          <span>Waiting for gesture...</span>
        </div>
      </div>
      <div class="tutorial-footer">
        <button class="tutorial-skip-btn" id="tutorial-skip-btn">Skip Tutorial</button>
      </div>
    `;

    this.tutorialElement.querySelector('#tutorial-skip-btn')?.addEventListener('click', () => {
      TutorialManager.markComplete();
      this.stop();
      if (this.onComplete) this.onComplete();
    });
  }

  isRunning(): boolean {
    return this.isActive;
  }

  getSteps(): TutorialStep[] {
    return [...TUTORIAL_STEPS];
  }
}

/**
 * Creates the gesture reference overlay (togglable in-game help)
 */
export function createGestureReferenceOverlay(): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'gesture-reference-overlay hidden';
  overlay.id = 'gesture-reference';
  overlay.innerHTML = `
    <div class="gesture-ref-header">
      <h3>Gesture Reference</h3>
      <button class="gesture-ref-close" id="gesture-ref-close" aria-label="Close gesture reference">&times;</button>
    </div>
    <div class="gesture-ref-grid">
      ${TUTORIAL_STEPS.map(step => `
        <div class="gesture-ref-item" style="--ref-color: ${step.color}">
          <span class="gesture-ref-icon">${step.icon}</span>
          <span class="gesture-ref-name">${step.title}</span>
          <span class="gesture-ref-gesture">${formatGestureName(step.gesture)}</span>
        </div>
      `).join('')}
    </div>
  `;

  overlay.querySelector('#gesture-ref-close')?.addEventListener('click', () => {
    overlay.classList.add('hidden');
  });

  return overlay;
}

function formatGestureName(gesture: GestureShape): string {
  switch (gesture) {
    case 'circle': return 'Circle';
    case 'swipe-down': return 'Swipe Down';
    case 'swipe-up': return 'Swipe Up';
    case 'palm-hold': return 'Palm Hold';
    case 'zigzag': return 'Zigzag';
    default: return gesture;
  }
}
